import { useCallback, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';
import take from 'lodash/take';
import filter from 'lodash/filter';

import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilters
} from '../../../../../../products/productsTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { useProductsSeasonalFilter } from '../../../../../../products/components/filters/ProductsSeasonalFilter/hooks/useProductsSeasonalFilter';

import { ThreeDStockProductsFiltersSeasonalFilterItem } from './components/ThreeDStockProductsFiltersSeasonalFilterItem';

import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import { AlertMessage } from '../../../../../../../helpers/AlertMessage';

import { categoriesKeys, words } from '../../../../../../../locales/keys';

const INITIAL_TAGS_COUNT = 6;
const fieldName = FetchProductsFilterFields.SEASONAL_ID;

interface ThreeDStockProductsFiltersSeasonalFilterProps {
  productsFilters: FetchProductsFilters;
  changeProductsFilters: ChangeProductsFiltersFunc;
}

function ThreeDStockProductsFiltersSeasonalFilter({
  changeProductsFilters,
  productsFilters
}: ThreeDStockProductsFiltersSeasonalFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [minimized, setMinimized] = useState<boolean>(true);

  const toggleIsOpen = useCallback<() => void>(
    () => setIsOpen((prevState) => !prevState),
    []
  );

  const toggleMinimize = useCallback<() => void>(() => {
    setMinimized((prevState) => !prevState);
  }, [setMinimized]);

  const filterValue: string[] = useMemo(
    () => productsFilters[fieldName]?.in || [],
    [productsFilters]
  );

  const {
    allProductsSeasonalFilterData,
    productSeasonalFilterErrorMessage,
    productSeasonalFilterFetched,
    productSeasonalFilterIsPlaceholderData,
    handleChangeProductsSeasonalFilter
  } = useProductsSeasonalFilter({
    clientIds: Array.isArray(productsFilters?.clientIds)
      ? productsFilters.clientIds
      : undefined,
    selectedIds: filterValue,
    name: fieldName,
    productsFilters,
    changeProductsFilters
  });

  const handleCheckedSeasonal = useCallback(
    (valueId: string, checked: boolean) => {
      const newValues = checked
        ? [...filterValue, valueId]
        : filter(filterValue, (value) => value !== valueId);

      handleChangeProductsSeasonalFilter(
        {
          [fieldName]: newValues
        },
        size(newValues) > 0 ? [] : [fieldName]
      );
    },
    [filterValue, handleChangeProductsSeasonalFilter]
  );

  if (
    (productSeasonalFilterIsPlaceholderData || productSeasonalFilterFetched) &&
    isEmpty(allProductsSeasonalFilterData)
  ) {
    return null;
  }

  return (
    <div className="p-4">
      <div className="flex flex-col gap-3">
        <ButtonHelper
          className="flex items-center gap-2 rounded-sm"
          dataGa="seasonal_filter"
          onClick={toggleIsOpen}
        >
          <span className="text-sm font-semibold text-left flex-1">
            <Translate id={categoriesKeys.seasonal} />
          </span>

          <Icon
            icon={isOpen ? IconsEnum.CHEVRON_DOWN_SOLID : IconsEnum.CHEVRON_UP}
            className="h-5 w-5 text-gray-500"
          />
        </ButtonHelper>

        <AlertMessage message={productSeasonalFilterErrorMessage} />

        {isOpen && (
          <ul>
            {(minimized
              ? take(allProductsSeasonalFilterData, INITIAL_TAGS_COUNT)
              : allProductsSeasonalFilterData
            )?.map((value, index) => (
              <ThreeDStockProductsFiltersSeasonalFilterItem
                key={index}
                checked={filterValue?.includes(value.id)}
                onCheckedTag={handleCheckedSeasonal}
                tag={value}
              />
            ))}

            {size(filterValue) > INITIAL_TAGS_COUNT && (
              <PureIconButtonHelper
                className="p-0 space-x-1 flex gap-1 text-gray-900 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-100 leading-5 focus:ring-base text-current hover:underline transition-colors duration-150 ease-out"
                i18nText={minimized ? words.showAll : words.showLess}
                i18nTextClassName="text-sm underline"
                onClick={toggleMinimize}
                icon={
                  minimized
                    ? IconsEnum.CHEVRON_DOWN_SOLID
                    : IconsEnum.CHEVRON_UP
                }
                iconClassName="w-5 h-5"
              />
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ThreeDStockProductsFiltersSeasonalFilter;
